import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { orgs } from '@/api/catalogs';
import DatePopover from '@/components/DatePopover';
import { pickDefaultOrg } from '@/store/defaults';

type Granularity = 'day' | 'week' | 'month';

interface CashFlowItem {
  item_id: string | null;
  item_code: string | null;
  item_name: string;
  values: Record<string, number>;
  total: number;
}

interface CashFlowReport {
  period_start: string;
  period_end: string;
  granularity: Granularity;
  periods: string[];
  inflows: CashFlowItem[];
  outflows: CashFlowItem[];
  period_totals: {
    inflows: Record<string, number>;
    outflows: Record<string, number>;
    net: Record<string, number>;
  };
  totals: {
    inflow: number;
    outflow: number;
    net: number;
    start_balance: number;
    end_balance: number;
  };
}

const fmt = (v: number) => v === 0 ? '—' :
  v.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function CashFlowReportPage() {
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const [orgId, setOrgId] = useState('');
  const [granularity, setGranularity] = useState<Granularity>('month');
  const today = new Date();
  const [periodStart, setPeriodStart] = useState(
    new Date(today.getFullYear(), 0, 1).toISOString().slice(0, 10),
  );
  const [periodEnd, setPeriodEnd] = useState(today.toISOString().slice(0, 10));

  useEffect(() => {
    if (!orgId && orgList.data?.items[0]) setOrgId(pickDefaultOrg(orgList.data.items)!.id);
  }, [orgList.data, orgId]);

  const reportQ = useQuery({
    queryKey: ['cash-flow', orgId, periodStart, periodEnd, granularity],
    queryFn: () => api.get<CashFlowReport>('/cash-flow-report', {
      params: {
        organization_id: orgId,
        period_start: periodStart,
        period_end: periodEnd,
        granularity,
      },
    }).then((r) => r.data),
    enabled: !!orgId,
  });

  const data = reportQ.data;

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Рух грошових коштів (управлінський)</h2>
        <button onClick={() => window.print()}>🖨 Друкувати</button>
      </div>

      <div className="doc-grid" style={{ marginBottom: '1rem' }}>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Деталізація</label>
          <select value={granularity} onChange={(e) => setGranularity(e.target.value as Granularity)}>
            <option value="day">По днях</option>
            <option value="week">По тижнях</option>
            <option value="month">По місяцях</option>
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Організація</label>
          <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
            {orgList.data?.items.map((o) =>
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>)}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Період з</label>
          <DatePopover value={periodStart} onChange={(v) => setPeriodStart(v)} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Період по</label>
          <DatePopover value={periodEnd} onChange={(v) => setPeriodEnd(v)} />
        </div>
      </div>

      {data && (
        <>
          {/* KPI карточки */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.75rem',
            marginBottom: '1rem', padding: '0.75rem',
            background: 'var(--bg-soft-2)', borderRadius: 'var(--radius)',
          }}>
            <KPI label="На початок" value={fmt(data.totals.start_balance)} />
            <KPI label="Надходження" value={fmt(data.totals.inflow)} positive />
            <KPI label="Витрати" value={fmt(data.totals.outflow)} negative />
            <KPI label="Чистий рух"
                 value={fmt(data.totals.net)}
                 positive={data.totals.net > 0}
                 negative={data.totals.net < 0} />
            <KPI label="На кінець" value={fmt(data.totals.end_balance)} highlight />
          </div>

          {/* Таблиця */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', fontSize: '0.85rem', minWidth: '100%' }}>
              <thead>
                <tr style={{ background: 'var(--bg-soft-2)' }}>
                  <th style={{ padding: '0.5rem', textAlign: 'left', minWidth: 240,
                                position: 'sticky', left: 0, background: 'var(--bg-soft-2)' }}>
                    Стаття
                  </th>
                  {data.periods.map((p) => (
                    <th key={p} style={{ padding: '0.5rem', textAlign: 'right', minWidth: 110 }}>
                      {p}
                    </th>
                  ))}
                  <th style={{ padding: '0.5rem', textAlign: 'right', minWidth: 130,
                                background: 'var(--primary-soft)' }}>
                    Усього
                  </th>
                </tr>
              </thead>
              <tbody>
                {/* Надходження */}
                <SectionHeader title="📥 НАДХОДЖЕННЯ" colSpan={data.periods.length + 2} />
                {data.inflows.map((l, i) => (
                  <Row key={`in-${i}`} line={l} periods={data.periods} positive />
                ))}
                <TotalRow label="Разом надходжень"
                          values={data.period_totals.inflows}
                          periods={data.periods}
                          total={data.totals.inflow}
                          positive />

                {/* Витрати */}
                <SectionHeader title="📤 ВИТРАТИ" colSpan={data.periods.length + 2} />
                {data.outflows.map((l, i) => (
                  <Row key={`out-${i}`} line={l} periods={data.periods} negative />
                ))}
                <TotalRow label="Разом витрат"
                          values={data.period_totals.outflows}
                          periods={data.periods}
                          total={data.totals.outflow}
                          negative />

                {/* Чистий рух */}
                <TotalRow label="ЧИСТИЙ РУХ"
                          values={data.period_totals.net}
                          periods={data.periods}
                          total={data.totals.net}
                          big />
              </tbody>
            </table>
          </div>
          <p style={{ marginTop: '1rem', fontSize: '0.8rem', color: 'var(--muted)' }}>
            ⓘ Залишки грошей — сальдо рахунків 30, 31 на дату.
            Якщо у документів банк/каса не вказана стаття ДДС — рядок «Без статті».
          </p>
        </>
      )}
    </div>
  );
}

function KPI({ label, value, highlight, positive, negative }: any) {
  const color = highlight ? 'var(--primary)' :
                 positive ? 'var(--success)' :
                 negative ? 'var(--danger)' : 'var(--text)';
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>
        {label}
      </div>
      <div style={{ fontSize: '1.25rem', fontWeight: 700, marginTop: '0.2rem', color }}>
        {value}
      </div>
    </div>
  );
}

function SectionHeader({ title, colSpan }: { title: string; colSpan: number }) {
  return (
    <tr style={{ background: 'var(--bg-soft)' }}>
      <td colSpan={colSpan} style={{
        padding: '0.5rem', fontWeight: 700, fontSize: '0.85rem',
        color: 'var(--text-secondary)', textTransform: 'uppercase',
        letterSpacing: '0.05em',
      }}>
        {title}
      </td>
    </tr>
  );
}

function Row({ line, periods, positive, negative }: any) {
  const color = positive ? 'var(--success)' : negative ? 'var(--danger)' : 'inherit';
  return (
    <tr style={{ borderTop: '1px solid var(--border-soft)' }}>
      <td style={{
        padding: '0.4rem 0.5rem',
        position: 'sticky', left: 0, background: 'var(--bg-panel)',
      }}>
        <span style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'var(--muted)', marginRight: '0.4rem' }}>
          {line.item_code ?? '—'}
        </span>
        {line.item_name}
      </td>
      {periods.map((p: string) => (
        <td key={p} style={{ padding: '0.4rem 0.5rem', textAlign: 'right',
                              fontVariantNumeric: 'tabular-nums' }}>
          {fmt(line.values[p] || 0)}
        </td>
      ))}
      <td style={{
        padding: '0.4rem 0.5rem', textAlign: 'right', fontWeight: 600,
        color, background: 'var(--primary-soft)', fontVariantNumeric: 'tabular-nums',
      }}>
        {fmt(line.total)}
      </td>
    </tr>
  );
}

function TotalRow({ label, values, periods, total, positive, negative, big }: any) {
  const color = positive ? 'var(--success)' :
                 negative ? 'var(--danger)' :
                 total > 0 ? 'var(--success)' : total < 0 ? 'var(--danger)' : 'inherit';
  return (
    <tr style={{
      background: big ? 'var(--primary-soft)' : 'var(--bg-soft-2)',
      fontWeight: 700,
      borderTop: big ? '2px solid var(--primary)' : '1px solid var(--border)',
    }}>
      <td style={{
        padding: '0.5rem',
        position: 'sticky', left: 0,
        background: big ? 'var(--primary-soft)' : 'var(--bg-soft-2)',
      }}>
        {label}
      </td>
      {periods.map((p: string) => (
        <td key={p} style={{
          padding: '0.5rem', textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
          color: big ? (values[p] > 0 ? 'var(--success)' : values[p] < 0 ? 'var(--danger)' : 'inherit') : color,
        }}>
          {fmt(values[p] || 0)}
        </td>
      ))}
      <td style={{
        padding: '0.5rem', textAlign: 'right',
        fontVariantNumeric: 'tabular-nums',
        background: big ? 'var(--primary)' : 'transparent',
        color: big ? 'white' : color,
      }}>
        {fmt(total)}
      </td>
    </tr>
  );
}
